import {
  EnterpriseRatingSeverity,
  GetApplicationsQuery,
  GetBindPageQuery,
  GetCountriesQuery,
  GetCoveragesQuery,
  GetDomainListQuery,
  GetDomainsQuery,
  GetEntitiesQuery,
  GetIndustrySectorsQuery,
  GetIpAddressListQuery,
  GetIpAddressOpenPortsQuery,
  GetIpAddressQuery,
  GetNotesQuery,
  GetPolicyPageQuery,
  GetPortListQuery,
  GetQuotesPageQuery,
  GetQuotesQuery,
  GetRaterQuery,
  GetSoftwareListQuery,
  GetSubmissionCountQuery,
  GetSubmissionQuery,
  GetSubmissionsQuery,
  GetVulnerabilitiesQuery,
  GetVulnerabilityListQuery,
  IpAddressesAttackSurfaceQueryQuery,
  NoteInput,
  PageInfo,
  Scalars,
  SignificantTermsConditions,
} from '../gql/graphql';

import { IQuoteLineItemFeatures } from './utils.quote-line-item-features';
import { toTitleCase } from '@converge/data';

export interface ExcessParticipant {
  uuid: string;
  limit: number;
  companyShare?: null | number;
  attachmentPoint?: null | number;
  minimumPremium?: null | number;
  benchmarkPremium?: null | number;
  boundPremium?: null | number;
  boundIncreasedLimitFactor?: null | number;
  boundUnderlyingPercent?: null | number;
}

export interface ExcessLayer {
  position: number;
  name: string;
  limit: number;
  companyShare: number;
  attachmentPoint: number;
  minimumPremium: null | number;
  benchmarkPremium: number | null;
  boundPremium: number;
  boundUnderlyingPercent: null;
  boundIncreasedLimitFactor: null;
  participants: ExcessParticipant[];
}

export interface ExcessScheduleRating {
  modifier: number;
  percentage: number;
  minPercentage: number;
  maxPercentage: number;
  ratings: {
    modifier: number;
    percentage: number;
    minPercentage: number;
    maxPercentage: number;
    ratings: [];
  }[];
}

export interface ExcessSignificantTermsConditions {
  endorsementImpact: string;
  ratedModifier: number;
  selectedModifier: null;
  modifier: number;
  minModifier: number;
  maxModifier: number;
}

export interface IUnderwritingSocketResultsExcessOutput {
  convergeQuotedRatedRatio: number;
  convergeQuotedPremium: number;
  convergeRatedPremium: number;
  quotedRatedRatio: number;
  convergeLimit: number;
  convergePartOfLimit: number;
  convergeAttachmentPoint: number;
  convergeQuotaShare: number;
  convergeUnderlyingPremiumPercent: number;
  convergeIncreasedLimitFactor: number;
  convergeQuotedToBenchmarkRatio: number;
  selectedCorrectiveFactor: number;
  minCorrectiveFactor: number;
  maxCorrectiveFactor: number;
  correctiveFactor: number;
  scheduleRating: ExcessScheduleRating;
  significantTermsConditions: ExcessSignificantTermsConditions;
  tower: ExcessLayer[];
}

export {};

export const EnterpriseRatingSeverityDefault =
  EnterpriseRatingSeverity.Moderate;

export const RISK_LEVEL_OPTIONS = Object.entries(EnterpriseRatingSeverity).map(
  (riskLevel) => {
    return {
      value: riskLevel[1],
      label: toTitleCase(riskLevel[1]),
    };
  }
);

export interface NavigationItem {
  disabled?: boolean;
  label: string;
  value: string;
  subItems?: NavigationItem[];
  icon?: string;
}

export type PaginatedEdge<Data = unknown> = {
  __typename?: string;
  cursor?: Scalars['Int'];
  node: Data;
};

export type PaginatedEdges<
  Data = unknown,
  PI extends Partial<PageInfo> = Partial<PageInfo>,
  Edge extends PaginatedEdge<Data> = PaginatedEdge<Data>
> = {
  __typename?: string;
  edges: Array<Edge>;
  pageInfo?: PI;
};

export type PaginatedEdgesWithPageInfo<
  Data = unknown,
  PI extends Pick<PageInfo, 'totalResults'> = Pick<PageInfo, 'totalResults'>,
  Edge extends PaginatedEdge<Data> = PaginatedEdge<Data>
> = Omit<PaginatedEdges<Data, PI, Edge>, 'pageInfo' | 'edges'> & {
  edges?: Array<Edge>;
  pageInfo: PI;
};

export type Flatten<T> = T extends unknown[] ? T[number] : T;

export type FlattenPagination<T extends PaginatedEdges> = Flatten<T['edges']>;

export type FirstPaginationNode<T extends PaginatedEdges> =
  FlattenPagination<T>['node'];

export interface IQueryGetEntitiesFieldArgs {
  submissionUuid: string;
  pageNumber: number;
  pageSize: number;
  entityUuid?: string;
}

export type EntitiesDomainsData = FirstPaginationNode<
  FirstPaginationNode<GetDomainsQuery['entities']>['domains']
>;

export type EntitiesDomainsSubdomainData = FirstPaginationNode<
  EntitiesDomainsData['subDomains']
>;

export type EntitiesIPAddressesData = FirstPaginationNode<
  FirstPaginationNode<GetIpAddressQuery['entities']>['ipAddresses']
>;

export type EntitiesIPAddressAttackSurfaceData = FirstPaginationNode<
  IpAddressesAttackSurfaceQueryQuery['ipAddressesAttackSurface']
>;

export type IPAddressAttackSurfaceData = FirstPaginationNode<
  FirstPaginationNode<GetIpAddressQuery['entities']>['ipAddresses']
>;

export type EntitiesIPAddressOpenPortsData = FirstPaginationNode<
  GetIpAddressOpenPortsQuery['entities']
>;

export type EntitiesSoftwareData = FirstPaginationNode<
  FirstPaginationNode<GetVulnerabilitiesQuery['entities']>['softwares']
>;

export type EntitiesVulnerabilitiesData = FirstPaginationNode<
  EntitiesSoftwareData['vulnerabilities']
>;

export type EntitiesCoveragesDataList = FirstPaginationNode<
  GetCoveragesQuery['entities']
>;

export type EntitiesCoverageData = FirstPaginationNode<
  EntitiesCoveragesDataList['coverages']
>;

export type CoverageControlDetails = FirstPaginationNode<
  EntitiesCoverageData['controls']
>;

export type CoverageSubControlDetails = FirstPaginationNode<
  CoverageControlDetails['subControls']
>;

export type EntitiesCoverageControlsData = FirstPaginationNode<
  EntitiesCoverageData['controls']
>;

export type EntitiesCoverageSubControlsData = FirstPaginationNode<
  EntitiesCoverageControlsData['subControls']
>;

export type ApplicationDetails = FirstPaginationNode<
  GetApplicationsQuery['applications']
>;

export type CountryDetails = FirstPaginationNode<
  GetCountriesQuery['countries']
>;

export type IndustrySectorDetails = FirstPaginationNode<
  GetIndustrySectorsQuery['industrySectors']
>;

export type QuoteDetails = FirstPaginationNode<GetQuotesQuery['quotes']>;

export type ParticipantCoverageDetails = FirstPaginationNode<
  RaterExcessTowerLayerParticipantDetails['coverages']
>;

export type RaterParticipantCoverageDetails = Omit<
  FirstPaginationNode<RaterExcessTowerLayerParticipantDetails['coverages']>,
  'quote' | 'subCoverages'
> & {
  quote: RaterParticipantCoverageQuoteDetails;
  subCoverages: RaterParticipantSubCoverageDetails[];
  hasChanged?: boolean;
};

export type RaterParticipantCoverageQuoteDetails = NonNullable<
  FirstPaginationNode<
    RaterExcessTowerLayerParticipantDetails['coverages']
  >['quote']
>;

export type RaterParticipantSubCoverageDetails = Omit<
  FirstPaginationNode<
    FirstPaginationNode<
      RaterExcessTowerLayerParticipantDetails['coverages']
    >['subCoverages']
  >,
  'quote'
> & {
  quote: RaterParticipantSubCoverageQuoteDetails;
  hasChanged?: boolean;
};

export type RaterParticipantSubCoverageQuoteDetails = NonNullable<
  NonNullable<
    FirstPaginationNode<
      NonNullable<
        FirstPaginationNode<
          RaterExcessTowerLayerParticipantDetails['coverages']
        >
      >['subCoverages']
    >['quote']
  >
>;

export type CoverageDetails = Omit<
  FirstPaginationNode<QuoteDetails['coverages']>,
  'quote'
> & {
  quote: CoverageQuoteDetails;
};

export type CoverageQuoteDetails = NonNullable<
  FirstPaginationNode<QuoteDetails['coverages']>['quote']
>;

export type RaterUserQuotes = GetRaterQuery['userQuotes'];
export type RaterQuoteDetails = FirstPaginationNode<GetRaterQuery['quotes']>;
export type RaterPolicyDetails = FirstPaginationNode<GetRaterQuery['policies']>;

export type RaterCoverageDetails = Omit<
  FirstPaginationNode<RaterQuoteDetails['coverages']>,
  'quote'
> & {
  quote: RaterCoverageQuoteDetails;
};
export type RaterCoverageQuoteDetails = NonNullable<
  FirstPaginationNode<RaterQuoteDetails['coverages']>['quote']
>;
export type RaterSubCoverageDetails = NonNullable<
  FirstPaginationNode<RaterCoverageDetails['subCoverages']>
>;
export type RaterExcessTowerLayerDetails = NonNullable<
  FirstPaginationNode<RaterQuoteDetails['excessTowerLayers']>
>;
export type RaterExcessTowerLayerParticipantDetails = NonNullable<
  FirstPaginationNode<NonNullable<RaterExcessTowerLayerDetails['participants']>>
>;
export type RaterEnterpriseRatingDetails = Omit<
  FirstPaginationNode<RaterQuoteDetails['enterpriseRatings']>,
  'quote'
> & {
  quote: RaterEnterpriseRatingQuoteDetails;
};
export type RaterEnterpriseRatingQuoteDetails = NonNullable<
  FirstPaginationNode<RaterQuoteDetails['enterpriseRatings']>['quote']
>;
export type RaterScheduleRatingDetails = Omit<
  FirstPaginationNode<RaterQuoteDetails['scheduleRatings']>,
  'quote'
> & {
  quote: RaterScheduleRatingQuoteDetails;
};
export type RaterScheduleRatingQuoteDetails = NonNullable<
  FirstPaginationNode<RaterQuoteDetails['scheduleRatings']>['quote']
>;

export type RaterEndorsementDetails = Omit<
  FirstPaginationNode<RaterQuoteDetails['endorsements']>,
  'quote'
> & {
  quote: RaterEndorsementQuoteDetails;
};
export type RaterEndorsementQuoteDetails = NonNullable<
  FirstPaginationNode<RaterQuoteDetails['endorsements']>['quote']
>;

export type RaterSubjectivityDetails = Omit<
  FirstPaginationNode<RaterQuoteDetails['subjectivities']>,
  'quote'
> & {
  quote: RaterSubjectivityQuoteDetails;
};
export type RaterSubjectivityQuoteDetails = NonNullable<
  FirstPaginationNode<RaterQuoteDetails['subjectivities']>['quote']
>;

// TODO: Undo this weird type hack
export type SubmissionListData = FirstPaginationNode<
  GetSubmissionsQuery['submissions']
>;

export type SubmissionListApplicationData = FirstPaginationNode<
  SubmissionListData['applications']
>;

export type SubmissionDetailData = NonNullable<
  GetSubmissionQuery['submission']
>;
export type EntityDetailData = NonNullable<
  GetEntitiesQuery['entities']['edges'][0]['node']
>;

export type SubmissionDetailUserData = FirstPaginationNode<
  NonNullable<SubmissionDetailData['users']>
>;

export type SubmissionDetailInsuredEntityAliasData = FirstPaginationNode<
  NonNullable<SubmissionDetailData['insuredEntity']>['node']['aliases']
>;

export type SubmissionDetailInsuredEntityDomainData = FirstPaginationNode<
  NonNullable<SubmissionDetailData['insuredEntity']>['node']['domains']
>;

export type SubmissionDetailInsuredEntityFundamentalsData = FirstPaginationNode<
  NonNullable<SubmissionDetailData['insuredEntity']>['node']['fundamentals']
>;

export type SubmissionDetailInsuredEntityAddressData = FirstPaginationNode<
  NonNullable<SubmissionDetailData['insuredEntity']>['node']['addresses']
>;

export type SubmissionDetailInsuredEntityPersonData = FirstPaginationNode<
  NonNullable<SubmissionDetailData['insuredEntity']>['node']['persons']
>;

export type SubmissionDetailInsuredEntityIndustrySectorData =
  FirstPaginationNode<
    NonNullable<
      SubmissionDetailData['insuredEntity']
    >['node']['industrySectors']
  >;

export type SubmissionDetailApplicationData = FirstPaginationNode<
  SubmissionDetailData['applications']
>;

export type SubmissionCountDetails = Flatten<
  | GetSubmissionCountQuery['inProgress']['pageInfo']['totalResults']
  | GetSubmissionCountQuery['completed']['pageInfo']['totalResults']
>;

export interface IUnderwritingQuote
  extends Omit<
    RaterQuoteDetails,
    | 'coverages'
    | 'subjectivities'
    | 'endorsements'
    | 'scheduleRatings'
    | 'enterpriseRatings'
    | 'excessTowerLayers'
  > {
  coverages: IUnderwritingCoverage[];
  subjectivities: IUnderwritingSubjectivity[];
  endorsements: IUnderwritingEndorsement[];
  enterpriseRatings: IUnderwritingEnterpriseRating[];
  scheduleRatings: IUnderwritingScheduleRating[];
  endorsementImpact: SignificantTermsConditions;
  hasEndorsementImpactChanged?: boolean;
  hasStcSelectedModifierChanged?: boolean;
  hasStcExcessSelectedModifierChanged?: boolean;
  hasExcessEndorsementImpactChanged?: boolean;
  hasErSelectedFusionScoreModifierChanged?: boolean;
  minimumPremium: number;
  fusionScore?: number | null;
  excessTowerLayers: IUnderwritingQuoteExcessTowerLayer[];
  excessSelectedCorrectiveFactorNew: number | null;
  hasExcessSelectedCorrectiveFactorChanged?: boolean;
  hasChanged?: boolean;
  changeReason?: string;
  inForcePolicy?: boolean;
  quotedRatedRatio?: number;
  srMinPercentage?: number;
  srMaxPercentage?: number;
  srMinItemPercentage?: number;
  srMaxItemPercentage?: number;
}

export interface IUnderwritingQuoteExcessRating {
  limit: number;
  // selectedCorrectiveFactor: number | null; // 1.0
  // scheduleRatings: IUnderwritingScheduleRating[];
  endorsementImpact: SignificantTermsConditions; // 'NEUTRAL'
  tower: IUnderwritingQuoteExcessTowerLayer[];
  // stcSelectedModifier: number | null; // 1.0
}

export interface IUnderwritingQuoteExcessTowerLayer
  extends Omit<RaterExcessTowerLayerDetails, 'participants'> {
  isNew: boolean;
  hasChanged?: boolean;
  // uuid: string;
  // position: number;
  // limit: number; // 2000000
  participants: IUnderwritingQuoteExcessLayerParticipant[];
  // premium: number; // 10000
  // name: string;
}

export interface IUnderwritingQuoteExcessLayerParticipant
  extends Omit<
      RaterExcessTowerLayerParticipantDetails,
      'aliases' | 'quote' | 'coverages'
    >,
    Pick<Partial<RaterExcessTowerLayerParticipantDetails>, 'aliases'> {
  isNew: boolean;
  hasChanged?: boolean;
  name: string;
  coverages: RaterParticipantCoverageDetails[];
  quote: RaterExcessTowerLayerParticipantDetails['quote'];
  // uuid: string; // 4ce33334-f0aa-4caf-b79c-e33b21e581fb | Cowbell | Coco | QBE | Sompo
  // limit: number; // 500000 | 250000 | 250000 | 10000 | 1500000 | 500000
  // name: string;
  // fusionScore?: number | null;
}

export interface IUnderwritingScheduleRating
  extends RaterScheduleRatingDetails {
  hasChanged?: boolean;
}

export interface IUnderwritingEnterpriseRating
  extends RaterEnterpriseRatingDetails {
  hasChanged?: boolean;
}

export interface IUnderwritingSubjectivity
  extends Omit<RaterSubjectivityDetails, 'quote'> {
  hasChanged?: boolean;
  quote: RaterSubjectivityDetails['quote'];
  isNew?: boolean;
}
export interface IUnderwritingCoverage
  extends Omit<
    RaterCoverageDetails,
    'quote' | 'subCoverages' | 'endorsements'
  > {
  hasChanged?: boolean;
  features: IQuoteLineItemFeatures;
  slug?: UnderwritingRatingId;
  endorsements: IUnderwritingEndorsement[] | null | undefined;
  // enableRetention?: boolean;
  // enableWaitingPeriodHours?: boolean;
  // enableWaitingPeriodDays?: boolean;
  // enableRestorationPeriodDays?: boolean;
  // showRetroactiveDateFields?: boolean;
  quote: RaterCoverageDetails['quote'] & {
    waitingPeriodDays?: number;
    subLimit?: number;
    subRetention?: number;
  };
  subCoverages: IUnderwritingSubCoverage[];
}

export interface IUnderwritingSubCoverage
  extends Omit<RaterSubCoverageDetails, 'quote'> {
  features: IQuoteLineItemFeatures;
  hasChanged?: boolean;
  // enableRetention?: boolean;
  // enableWaitingPeriodDays?: boolean;
  quote: RaterSubCoverageDetails['quote'] & {
    waitingPeriodDays?: number;
    restorationPeriodDays?: number;
    waitingPeriodHours?: number;
  };
}

export interface IUnderwritingEndorsement
  extends Omit<RaterEndorsementDetails, 'quote' | 'questions'> {
  hasChanged?: boolean;
  modifiedCoveragePremium?: number;
  features?: IQuoteLineItemFeatures;
  isNew?: boolean;
  questions: RaterEndorsementDetails['questions'] & {
    edges:
      | {
          cursor?: number;
          node: RaterEndorsementDetails['questions']['edges'][0]['node'];
        }[]
      | [];
  };
  quote: RaterEndorsementDetails['quote'] & {
    waitingPeriodDays?: number;
  };
}

export interface IUnderwritingEndorsementQuote
  extends Pick<RaterEndorsementDetails, 'quote'> {
  limit: number;
  retention: number;
  hazardGroup: number;
  basePremium: number;
  technicalPremium: number;
  modifiedCoveragePremium: number;

  included?: boolean;
  waitingPeriodHours?: number;
  restorationPeriodDays?: number;
  limitRetentionFactor?: number;
  policyTermFactor?: number;
  commissionAdjustmentFactor?: number;
  priorActsFactor?: number;
  restorationPeriodFactor?: number;
  waitingPeriodFactor?: number;
}

export enum UnderwritingRatingId {
  PrivacyLiability = 'privacy_liability',
  NetworkSecurityLiability = 'network_security_liability',
  MediaLiability = 'media_liability',
  RegulatoryProceedings = 'regulatory_proceedings',
  PciDssEvents = 'pci_dss_events',
  IncidentResponse = 'incident_response',
  BusinessInterruption = 'business_interruption',
  ContingentBusinessInterruption = 'contingent_business_interruption',
  CyberExtortion = 'cyber_extortion',
  DigitalAssetRecovery = 'digital_asset_recovery',
  BrickedEquipmentReplacement = 'bricked_equipment_replacement',
  ReputationalHarm = 'reputational_harm',
  CyberCrime = 'cyber_crime',
  TechnologyProfessionalLiability = 'technology_professional_liability',
  MiscellaneousProfessionalLiability = 'miscellaneous_professional_liability',
  Unknown = 'unknown',
}

export type NotesListData = GetNotesQuery['notes'];
export type NotesListDetailData = FirstPaginationNode<NotesListData>;

export enum NoteTargetName {
  HasCoverageType = 'HAS_COVERAGE',
  HasEndorsementType = 'HAS_ENDORSEMENT',
  UnderwritingQueue = 'UNDERWRITING_QUEUE',
  Submission = 'Submission',
  HasSubjectivityType = 'HAS_SUBJECTIVITY',
  HasSubCoverageType = 'HAS_SUB_COVERAGE',
  HasScheduleRating = 'HAS_SCHEDULE_RATING',
  HasEnterpriseRating = 'HAS_ENTERPRISE_RATING',
  ExcessTowerLayer = 'EXCESS_TOWER_LAYER',
  ExcessTowerLayerParticipant = 'EXCESS_TOWER_LAYER_PARTICIPANT',
  Quote = 'Quote',
}

export enum SubmissionNoteLabel {
  DescriptionOfOperations = 'Description_of_operations',
  prosAndCons = 'pros_and_cons',
  FinancialAnalysisSummary = 'Financial_analysis_summary',
}

export type CreateNoteTargetInput = Pick<
  NoteInput,
  'targetUuid' | 'targetType' | 'targetName'
>;
export type CreateContentTargetInput = Pick<
  NoteInput,
  'targetUuid' | 'targetType' | 'targetName'
>;

export type BindPageSubjectivity = FirstPaginationNode<
  FirstPaginationNode<GetBindPageQuery['quotes']>['subjectivities']
>;

export type BindPageQuote = FirstPaginationNode<GetBindPageQuery['quotes']>;

export type PolicyPageQuote = FirstPaginationNode<GetPolicyPageQuery['quotes']>;

export type BindOrPolicyPageQuote = BindPageQuote | PolicyPageQuote;

export type QuotesPageQuote = FirstPaginationNode<GetQuotesPageQuery['quotes']>;
export interface IInsuredEntityIpAddressOpenPorts {
  ip?: string | null;
  protocol?: string;
  port?: number;
  isFirstRecord: boolean;
  totalRecords: number;
}

export interface IQueryGetEntityFieldArgs {
  entityUuid: string;
  pageNumber: number;
  pageSize: number;
}

export type DomainList = FirstPaginationNode<
  NonNullable<GetDomainListQuery['domains']>
>;
export type SubDomainList = FirstPaginationNode<DomainList['subDomains']>;

export type PortList = FirstPaginationNode<
  NonNullable<GetPortListQuery['ports']>
>;

export type VulnerabilityList = FirstPaginationNode<
  NonNullable<GetVulnerabilityListQuery['vulnerabilities']>
>;

export type SoftwareList = FirstPaginationNode<
  NonNullable<GetSoftwareListQuery['softwares']>
>;
export type IpAddressList = FirstPaginationNode<
  NonNullable<GetIpAddressListQuery['ipAddresses']>
>;

export enum ModifierNoteLabel {
  FusionScore = 'er_fusion_score_modifier',
  ScheduleRating = 'sr_modifier',
  StcExcess = 'stc_excess_modifier',
  StcPrimary = 'stc_primary_modifier',
}
